import React from 'react';
import { BarChart3, TrendingUp, Clock, Target, Users, ArrowRight } from 'lucide-react';

/**
 * JobPipelineAnalytics - Job-specific analytics with conversion funnel and insights
 * Features: Conversion rates, stage timings, visual funnel, recommendations
 */
const JobPipelineAnalytics = ({ 
  isVisible = false, 
  onToggle, 
  selectedJob = null,
  analytics = {},
  loading = false 
}) => {
  const {
    totalApplications = 0,
    stageDistribution = {},
    averageTimeToHire = 0,
    conversionRates = {},
    averageScore = 0
  } = analytics;

  const stages = [
    { id: 'new', name: 'Applied', color: 'bg-blue-500', textColor: 'text-blue-700' },
    { id: 'screening', name: 'Screening', color: 'bg-amber-500', textColor: 'text-amber-700' },
    { id: 'interview', name: 'Interview', color: 'bg-purple-500', textColor: 'text-purple-700' },
    { id: 'final-review', name: 'Final Review', color: 'bg-indigo-500', textColor: 'text-indigo-700' },
    { id: 'offer', name: 'Offer', color: 'bg-emerald-500', textColor: 'text-emerald-700' },
    { id: 'hired', name: 'Hired', color: 'bg-green-600', textColor: 'text-green-700' }
  ];

  const conversionSteps = [
    { from: 'new', to: 'screening', rate: conversionRates?.new_to_screening || 0, label: 'Application → Screening' },
    { from: 'screening', to: 'interview', rate: conversionRates?.screening_to_interview || 0, label: 'Screening → Interview' },
    { from: 'interview', to: 'offer', rate: conversionRates?.interview_to_offer || 0, label: 'Interview → Offer' },
    { from: 'offer', to: 'hired', rate: conversionRates?.offer_to_hire || 0, label: 'Offer → Hired' }
  ];

  if (!isVisible) {
    return null;
  }

  if (!selectedJob) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Position Selected</h3>
          <p className="text-gray-600 mb-4">
            Please select a job position to view detailed analytics.
          </p>
          <button
            onClick={onToggle}
            className="px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-6">
              <div className="w-64 h-6 bg-gray-200 rounded" />
              <div className="w-16 h-8 bg-gray-200 rounded" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-xl mb-3" />
                  <div className="w-16 h-8 bg-gray-200 rounded mb-2" />
                  <div className="w-24 h-4 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Pipeline Analytics
            </h2>
            <p className="text-gray-600">
              {selectedJob?.title} • {selectedJob?.department}
            </p>
          </div>
          
          <button
            onClick={onToggle}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-violet-50 to-violet-100/60 border border-violet-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-violet-200 rounded-xl flex items-center justify-center">
                <Users className="w-4 h-4 text-violet-700" />
              </div>
              <div>
                <div className="text-2xl font-bold text-violet-900">{totalApplications}</div>
                <div className="text-xs text-violet-700">Total Applicants</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100/60 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-amber-200 rounded-xl flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-700" />
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-900">{Math.round(averageTimeToHire)}</div>
                <div className="text-xs text-amber-700">Avg Days to Hire</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-emerald-200 rounded-xl flex items-center justify-center">
                <Target className="w-4 h-4 text-emerald-700" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-900">
                  {averageScore > 0 ? Math.round(averageScore) : '-'}
                </div>
                <div className="text-xs text-emerald-700">Avg Score</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-200 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-700" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {stageDistribution?.hired || 0}
                </div>
                <div className="text-xs text-blue-700">Hired</div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-gray-50/80 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-violet-600" />
            Conversion Funnel
          </h3>
          
          <div className="space-y-4">
            {conversionSteps?.map((step, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{step?.label}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {stageDistribution?.[step?.from] || 0} → {stageDistribution?.[step?.to] || 0} candidates
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {step?.rate?.toFixed(1)}%
                      </div>
                      <div className={`text-xs font-medium ${
                        step?.rate >= 60 ? 'text-emerald-600' : 
                        step?.rate >= 40 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {step?.rate >= 60 ? 'Excellent' : step?.rate >= 40 ? 'Good' : 'Needs Attention'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        step?.rate >= 60 ? 'bg-emerald-500' : 
                        step?.rate >= 40 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(step?.rate, 100)}%` }}
                    />
                  </div>
                </div>
                
                {index < conversionSteps?.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stage Distribution */}
        <div className="bg-gray-50/80 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-600" />
            Stage Distribution
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {stages?.map((stage) => {
              const count = stageDistribution?.[stage?.id] || 0;
              const percentage = totalApplications > 0 ? (count / totalApplications) * 100 : 0;
              
              return (
                <div key={stage?.id} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className={`w-12 h-12 ${stage?.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                    <span className="text-white font-bold text-lg">{count}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {stage?.name}
                  </div>
                  <div className={`text-xs font-medium ${stage?.textColor}`}>
                    {percentage?.toFixed(1)}%
                  </div>
                  <div className="mt-2 bg-gray-100 rounded-full h-1 overflow-hidden">
                    <div
                      className={`${stage?.color} h-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPipelineAnalytics;